import React, { useState, useEffect } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrashAlt, faEye, faEdit } from "@fortawesome/free-solid-svg-icons";
import "../Assignmentpage/Assignmentpage.css";
import AddAssignment from "../AddAssignment/AddAssignment";
import ViewAssignment from "../ViewAssignment/ViewAssignment";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import moment from "moment";

const API_URL = "https://aadyaacademy.org/api";

const Assignmentpage = () => {
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [isAddAssignmentOpen, setIsAddAssignmentOpen] = useState(false);
  const [assignmentToEdit, setAssignmentToEdit] = useState(null);
  const [viewAssignment, setViewAssignment] = useState(null);

  useEffect(() => {
    fetchAssignments();
  }, []);

  const fetchAssignments = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/assignments`);
      // Sort by date in descending order (most recent first)
      const sortedData = response.data.sort((a, b) => {
        const dateA = new Date(a.date || "1970-01-01");
        const dateB = new Date(b.date || "1970-01-01");
        return dateB - dateA || (b.id || 0) - (a.id || 0); // Fallback to id if dates are equal
      });
      setTableData(sortedData);
      
    } catch (error) {
      console.error("Error fetching assignments:", error.response?.data || error.message);
      toast.error("Failed to fetch assignments. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => setSearchTerm(e.target.value);

  const handleAddAssignment = () => {
    setAssignmentToEdit(null);
    setIsAddAssignmentOpen(true);
  };

  const handleCloseAddAssignment = () => {
    setIsAddAssignmentOpen(false);
    setAssignmentToEdit(null);
    fetchAssignments(); // Refresh data after closing form
  };

  const handleEdit = (assignment) => {
    setAssignmentToEdit(assignment);
    setIsAddAssignmentOpen(true);
  };

  const handleView = (assignment) => {
    setViewAssignment(assignment);
  };

  const handleDelete = async (assignmentId) => {
    if (!window.confirm("Are you sure you want to delete this assignment?")) return;

    try {
      await axios.delete(`${API_URL}/assignments/${assignmentId}`);
      setTableData((prevData) => prevData.filter((item) => item.id !== assignmentId));
      toast.success("Assignment deleted successfully!");
    } catch (error) {
      console.error("Error deleting assignment:", error.response?.data || error.message);
      toast.error("Failed to delete assignment. Please try again.");
    }
  };

  const handleCloseView = () => {
    setViewAssignment(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return moment(dateString).format("DD/MM/YYYY");
  };

  const getDayLabel = (dateString) => {
    if (!dateString) return "N/A";
    const today = moment();
    const yesterday = moment().subtract(1, "days");
    const assignmentDate = moment(dateString);

    if (assignmentDate.isSame(today, "day")) return "Today";
    if (assignmentDate.isSame(yesterday, "day")) return "Yesterday";
    return assignmentDate.format("dddd");
  };

  const getAttachmentUrl = (url) => {
    if (!url) return null;
    // Extract the file path (e.g., 'uploads/rtyuiojkjhgfvcsaertyu.pdf') from the URL
    const filePath = url.split('/uploads/')[1];
    return filePath ? `${API_URL}/uploads/${filePath}` : null;
  };

  const handleAssignmentAdded = (newAssignment) => {
    // Ensure new assignment has a current date if not provided
    const assignmentWithDate = {
      ...newAssignment,
      date: newAssignment.date || new Date().toISOString(),
    };
    console.log("New assignment added:", assignmentWithDate); // Debug log
    setTableData((prevData) => 
      [assignmentWithDate, ...prevData].sort((a, b) => {
        const dateA = new Date(a.date || "1970-01-01");
        const dateB = new Date(b.date || "1970-01-01");
        return dateB - dateA || (b.id || 0) - (a.id || 0); // Fallback to id if dates are equal
      })
    );
    setIsAddAssignmentOpen(false);
    toast.success("Assignment added successfully!");
  };

  const filteredData = tableData.filter((item) =>
    [
      item.class || "",
      item.section || "",
      item.subject || "",
      item.date || "",
      getDayLabel(item.date) || "",
      item.text_message || "",
    ].some((field) =>
      field.toString().toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  // Sort filtered data by date in descending order
  const sortedData = filteredData.sort((a, b) => {
    const dateA = new Date(a.date || "1970-01-01");
    const dateB = new Date(b.date || "1970-01-01");
    return dateB - dateA || (b.id || 0) - (a.id || 0); // Fallback to id if dates are equal
  });

  const handleRowClick = (assignment, event) => {
    if (
      event.target.tagName === "BUTTON" ||
      event.target.tagName === "A" ||
      event.target.closest("button") ||
      event.target.closest("a")
    ) {
      return;
    }
    handleView(assignment);
  };

  return (
    <div className="assignment-page">
      {isAddAssignmentOpen ? (
        <div className="add-assignment-form">
          <AddAssignment
            closeForm={handleCloseAddAssignment}
            assignment={assignmentToEdit}
            onAssignmentAdded={handleAssignmentAdded}
          />
        </div>
      ) : viewAssignment ? (
        <div className="view-assignment-form">
          <ViewAssignment
            assignment={viewAssignment}
            closeDetail={handleCloseView}
          />
        </div>
      ) : (
        <div className="assignment-table">
          <div className="table-header">
            <h3>Assignments</h3>
            <div className="table-header-bottom">
              <input
                type="text"
                placeholder="Search assignments..."
                className="search-input"
                value={searchTerm}
                onChange={handleSearchChange}
              />
              
            </div>
          </div>

          {loading ? (
            <div className="loading">Loading assignments...</div>
          ) : sortedData.length === 0 ? (
            <div className="no-data">No assignments found</div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th className="thead-content">Grade</th>
                  <th className="thead-content">Section</th>
                  <th className="thead-content">Subject</th>
                  <th className="thead-content">Date</th>
                  <th className="thead-content">Day</th>
                  <th className="thead-content">Attachment/Message</th>
                  <th className="thead-content">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedData.map((assignment) => (
                  <tr
                    key={assignment.id}
                    onClick={(e) => handleRowClick(assignment, e)}
                    className="assignment-row"
                  >
                    <td className="tbody-content">{assignment.class || "N/A"}</td>
                    <td className="tbody-content">{assignment.section || "N/A"}</td>
                    <td className="tbody-content">{assignment.subject || "N/A"}</td>
                    <td className="tbody-content">{formatDate(assignment.date)}</td>
                    <td className="tbody-content">{getDayLabel(assignment.date)}</td>
                    <td className="tbody-content">
                      {assignment.text_message ? (
                        <span>{assignment.text_message}</span>
                      ) : assignment.attachmentUrl ? (
                        <a
                          href={getAttachmentUrl(assignment.attachmentUrl)}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                        >
                          View Attachment
                        </a>
                      ) : (
                        "No attachment/message"
                      )}
                    </td>
                    <td className="actions">
                      <button
                        className="view-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleView(assignment);
                        }}
                        title="View"
                      >
                        <FontAwesomeIcon icon={faEye} />
                      </button>
                      <button
                        className="edit-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(assignment);
                        }}
                        title="Edit"
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      <button
                        className="delete-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(assignment.id);
                        }}
                        title="Delete"
                      >
                        <FontAwesomeIcon icon={faTrashAlt} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
};

export default Assignmentpage;